import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, ReactNode, useEffect, useRef } from 'react';
import { ScrollContainer, VirtualizerItems, VirtualizerRoot } from './style';

interface VirtualizerProps {
    items: readonly ReactNode[];
    estimatedSize: number;
    overscan?: number;
    height?: number | 'fit-content';
    scrollToIndex?: number;
    onTotalSizeChange?: (size: number) => void;
    // ResizeObserver loop fix, see https://github.com/TanStack/virtual/pull/923
    preventResizeObserverLoop?: boolean;
}

export const Virtualizer = memo(
    ({
        items,
        estimatedSize,
        overscan = 3,
        scrollToIndex,
        height,
        onTotalSizeChange,
        preventResizeObserverLoop = false,
    }: VirtualizerProps) => {
        const parentRef = useRef<HTMLDivElement>(null);
        const virtualizer = useVirtualizer({
            count: items.length,
            getScrollElement: () => parentRef.current,
            estimateSize: () => estimatedSize,
            overscan,
            useAnimationFrameWithResizeObserver: preventResizeObserverLoop,
        });

        useEffect(() => {
            if (scrollToIndex !== undefined) {
                try {
                    virtualizer.scrollToIndex(scrollToIndex, {
                        align: 'center',
                        behavior: 'smooth',
                    });
                } catch {
                    if (process.env.NODE_ENV === 'development') {
                        // eslint-disable-next-line no-console
                        console.log(`Invalid index to scroll to in Virtualizer: "${scrollToIndex}"`);
                    }
                }
            }
        }, [virtualizer, scrollToIndex]);

        // Notify parent of total size changes
        useEffect(() => {
            onTotalSizeChange?.(virtualizer.getTotalSize());
        }, [items, onTotalSizeChange, virtualizer]);

        const virtualItems = virtualizer.getVirtualItems();

        return (
            <ScrollContainer ref={parentRef} containerHeight={height} data-test-id="virtualizer-container">
                <VirtualizerRoot totalSize={virtualizer.getTotalSize()} data-test-id="virtualizer-root">
                    <VirtualizerItems translateY={virtualItems[0]?.start ?? 0} data-test-id="virtualizer-items">
                        {virtualItems.map((virtualItem) => (
                            <div
                                key={virtualItem.key.toString()}
                                data-index={virtualItem.index}
                                ref={virtualizer.measureElement}
                                data-test-id={`virtualizer-item-${virtualItem.index}`}
                            >
                                {items[virtualItem.index]}
                            </div>
                        ))}
                    </VirtualizerItems>
                </VirtualizerRoot>
            </ScrollContainer>
        );
    }
);
